/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import Head from 'next/head';
import {
  type CSSProperties,
  type ImgHTMLAttributes,
  type ReactEventHandler,
  forwardRef,
  useId,
} from 'react';
import { BREAKPOINTS } from '@/constants/screen';

type ImageConfiguration = {
  src: string;
  width: CSSProperties['width'];
  height: CSSProperties['height'];
  transformation?: string;
};

export type PictureProps = {
  altText: string;
  loading?: ImgHTMLAttributes<HTMLImageElement>['loading'];
  fetchPriority?: ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  priority?: boolean;
  imageClassName?: string;
  mobile: ImageConfiguration;
  tablet: ImageConfiguration;
  desktop: ImageConfiguration;
  tabIndex?: number | undefined;
  'data-testid'?: string;
  onError?: ReactEventHandler<HTMLImageElement>;
  ref?: React.Ref<HTMLImageElement>;
  imageId?: string;
};

export const Picture = forwardRef<HTMLImageElement, PictureProps>(function PictureComponent(
  {
    mobile,
    tablet,
    desktop,
    loading = 'lazy',
    fetchPriority,
    imageClassName,
    altText,
    priority = false,
    tabIndex,
    'data-testid': dataTestId,
    onError,
    imageId,
  },
  ref,
) {
  const mobileMedia = `(max-width: ${BREAKPOINTS.MOBILE_MAX}px)`;
  const tabletMedia = `(min-width: ${BREAKPOINTS.TABLET_MIN}px) and (max-width: ${BREAKPOINTS.TABLET_MAX}px)`;
  const desktopMedia = `(min-width: ${BREAKPOINTS.DESKTOP_MIN}px)`;
  const hasTransformation = !!(
    mobile.transformation ||
    tablet.transformation ||
    desktop.transformation
  );
  const imageStyle: CSSProperties | undefined = hasTransformation
    ? { objectFit: 'cover', objectPosition: 'center' }
    : undefined;

  // Generate unique class for responsive height styles
  // Always use uniqueId (not imageId) to ensure uniqueness even when imageId is shared
  const uniqueId = useId();
  const sanitizedId = uniqueId.replace(/[^a-zA-Z0-9]/g, '-');
  const transformationClass = hasTransformation ? `picture-transform-${sanitizedId}` : '';

  return (
    <>
      {hasTransformation && (
        <style>
          {`
            .${transformationClass} {
              height: ${typeof desktop.height === 'number' ? `${desktop.height}px` : desktop.height};
            }
            @media ${tabletMedia} {
              .${transformationClass} {
                height: ${typeof tablet.height === 'number' ? `${tablet.height}px` : tablet.height};
              }
            }
            @media ${mobileMedia} {
              .${transformationClass} {
                height: ${typeof mobile.height === 'number' ? `${mobile.height}px` : mobile.height};
              }
            }
          `}
        </style>
      )}
      <picture>
        <source
          media={mobileMedia}
          srcSet={mobile.src}
          width={mobile.width}
          height={mobile.height}
        />
        <source
          media={tabletMedia}
          srcSet={tablet.src}
          width={tablet.width}
          height={tablet.height}
        />
        <img
          id={imageId}
          fetchPriority={priority ? 'high' : fetchPriority}
          width={desktop.width}
          height={desktop.height}
          loading={priority ? 'eager' : loading}
          src={desktop.src}
          alt={altText}
          aria-label={altText === '' ? undefined : altText}
          className={
            hasTransformation
              ? `${imageClassName ?? ''} ${transformationClass}`.trim()
              : imageClassName
          }
          style={imageStyle}
          decoding="async"
          data-testid={dataTestId}
          onError={onError}
          tabIndex={tabIndex}
          ref={ref}
        />
      </picture>
      {priority && (
        <Head>
          <link
            rel="preload"
            as="image"
            key={mobile.src}
            href={mobile.src}
            media={mobileMedia}
            // In React 18.2.0 or older, we must use lowercase prop
            // to avoid "Warning: Invalid DOM property". In React 18.3.0 or newer, we must use camelCase
            // prop to avoid "Warning: Invalid DOM property".
            // See https://github.com/facebook/react/pull/25927
            fetchPriority="high"
          />
          <link
            key={tablet.src}
            rel="preload"
            as="image"
            href={tablet.src}
            media={tabletMedia}
            fetchPriority="high"
          />
          <link
            key={desktop.src}
            rel="preload"
            as="image"
            href={desktop.src}
            media={desktopMedia}
            fetchPriority="high"
          />
        </Head>
      )}
    </>
  );
});
